import { Command } from "commander";
import { CLI_VERSION } from "../lib/version";
import { login } from "./login";
import { logout } from "./logout";
import { report } from "./report";
import { scan } from "./scan";
import { sync } from "./sync";
import { whoami } from "./whoami";

const program = new Command();

program
  .name("tktuner")
  .description(
    "Local-first token usage analytics for Claude Code power users.\nScans ~/.claude/projects session logs and shows where you waste tokens and quota.",
  )
  .version(CLI_VERSION);

program
  .command("scan")
  .description("Incrementally parse Claude Code session logs into the local database")
  .option("--claude-dir <path>", "Claude projects directory (default: ~/.claude/projects)")
  .option("--db <path>", "database file (default: ~/.local/share/tktuner/tktuner.db)")
  .option("--full", "ignore incremental state and reparse every file")
  .option("--verbose", "print each file as it is parsed")
  .action(async (opts) => {
    await scan(opts);
  });

program
  .command("report")
  .description("Analyze scanned data: cost by model/phase, waste, cache health, quota pressure")
  .option("--days <n>", "analysis window in days", "30")
  .option("--all", "analyze the entire history")
  .option("--project <filter>", "only sessions whose project name contains this substring")
  .option("--json", "machine-readable JSON output")
  .option("--no-color", "disable colors")
  .option("--db <path>", "database file (default: ~/.local/share/tktuner/tktuner.db)")
  .option("--prices <path>", "JSON file overriding the bundled model price table")
  .option("--debug-session <id>", "per-turn phase breakdown for one session (id or slug prefix)")
  .action(async (opts) => {
    await report(opts);
  });

program
  .command("login")
  .description("Connect this machine to your tktuner.com account (device flow)")
  .option("--api <url>", "API base URL (default: https://tktuner.com)")
  .action(async (opts) => {
    await login(opts);
  });

program
  .command("logout")
  .description("Disconnect this machine and delete local credentials")
  .action(async () => {
    await logout();
  });

program
  .command("whoami")
  .description("Show the connected account, device and sync state")
  .option("--db <path>", "database file (default: ~/.local/share/tktuner/tktuner.db)")
  .action(async (opts) => {
    await whoami(opts);
  });

program
  .command("sync")
  .description("Scan locally, then upload new usage metadata (never content) to your dashboard")
  .option("--quiet", "no output, always exit 0 (for the SessionEnd hook)")
  .option("--full", "re-push the entire local database (idempotent server-side)")
  .option("--dry-run", "show what would be uploaded without sending anything")
  .option("--no-scan", "skip the local scan step")
  .option("--db <path>", "database file (default: ~/.local/share/tktuner/tktuner.db)")
  .option("--claude-dir <path>", "Claude projects directory (default: ~/.claude/projects)")
  .option("--api <url>", "override the API base URL from credentials")
  .action(async (opts) => {
    // commander turns --no-scan into { scan: false }
    await sync({ ...opts, noScan: opts.scan === false });
  });

const hook = program
  .command("hook")
  .description("Manage the opt-in Claude Code SessionEnd hook (auto-sync when a session ends)");
hook
  .command("install")
  .description("Add the SessionEnd hook to ~/.claude/settings.json (merge-safe, backed up)")
  .option("--yes", "skip the confirmation prompt")
  .action(async (opts) => {
    const { hookInstall } = await import("./hook");
    await hookInstall(opts);
  });
hook
  .command("uninstall")
  .description("Remove only tktuner's hook entry, leaving everything else untouched")
  .action(async () => {
    const { hookUninstall } = await import("./hook");
    await hookUninstall();
  });
hook
  .command("status")
  .description("Show whether the auto-sync hook is installed")
  .action(async () => {
    const { hookStatus } = await import("./hook");
    hookStatus();
  });

program.parseAsync(process.argv).catch((err: unknown) => {
  console.error(err instanceof Error ? err.message : String(err));
  process.exitCode = 1;
});
